import React, {useEffect, useState} from "react";
import "../../../index.css"

const TEXT_PREVIEW_LENGTH = 920;

export default function ProjectDetail({project, onClose, onCategoryClick}) {
    const images = project.images?.length
        ? project.images
        : [project.image].filter(Boolean);

    const [index, setIndex] = useState(0);
    const [expanded, setExpanded] = useState(false);
    const [allCategories, setAllCategories] = useState([]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadCategories() {
            try {
                const response = await fetch("/api/categories.php", {
                    credentials: "include",
                    signal: controller.signal
                });

                if (!response.ok) {
                    throw new Error("Kategorien konnten nicht geladen werden");
                }

                const data = await response.json();
                setAllCategories(Array.isArray(data) ? data : []);
            } catch (error) {
                if (error.name !== "AbortError") {
                    console.error(error);
                }
            }
        }

        loadCategories();

        return () => controller.abort();
    }, []);

    const projectCategories = allCategories.filter(category =>
        (project.categories ?? []).includes(category.id)
    );

    const facts = project.facts ?? [];
    const description = project.description ?? "";

    const next = () => setIndex(i => (i + 1) % images.length);
    const prev = () => setIndex(i => (i - 1 + images.length) % images.length);

    const isLongText = description.length > TEXT_PREVIEW_LENGTH;

    const displayedText = !isLongText || expanded
        ? description
        : description.slice(0, TEXT_PREVIEW_LENGTH).trim() + "…";

    return (
        <div style={overlayStyle} onClick={onClose}>
            <div
                style={panelStyle}
                className="detail-panel"
                onClick={e => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    style={closeButtonStyle}
                    aria-label="Schließen"
                >
                    ×
                </button>

                <div style={imageColStyle} className="detail-image-col">
                    {images.length > 0 && (
                        <img
                            src={images[index]}
                            alt={`${project.title} – Bild ${index + 1}`}
                            style={imageStyle}
                        />
                    )}

                    {images.length > 1 && (
                        <>
                            <button
                                onClick={prev}
                                style={{...navButtonStyle, left: "12px"}}
                                aria-label="Vorheriges Bild"
                            >
                                ‹
                            </button>

                            <button
                                onClick={next}
                                style={{...navButtonStyle, right: "12px"}}
                                aria-label="Nächstes Bild"
                            >
                                ›
                            </button>

                            <div style={dotsStyle}>
                                {images.map((_, i) => (
                                    <span
                                        key={i}
                                        onClick={() => setIndex(i)}
                                        style={{
                                            ...dotStyle,
                                            opacity: i === index ? 1 : 0.35
                                        }}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
                <div style={textColStyle} className="detail-text-col">

                    <div style={titleContainerStyle}>
                        <h2 style={titleStyle} title={project.title}>
                            {project.title}
                        </h2>
                    </div>

                    {projectCategories.length > 0 && (
                        <div style={categoriesStyle}>
                            {projectCategories.map(category => (
                                <button
                                    type="button"
                                    key={category.id}
                                    onClick={() => onCategoryClick?.(category.id)}
                                    className="categoryStyle"
                                    style={{
                                        borderBottom: `1px solid ${category.color}`,
                                        color: category.color
                                    }}
                                    title={`Alle Projekte in ${category.name} anzeigen`}
                                >
                                    {category.name}
                                </button>
                            ))}
                        </div>
                    )}
                    <div style={scrollContentStyle} className="detail-scroll">

                        {facts.length > 0 && (
                            <section style={factsSectionStyle}>
                                <div style={factsListStyle}>
                                    {facts.map((fact, i) => (
                                        <div key={i} style={factRowStyle}>
                                            <span style={factLabelStyle}>
                                                {fact.label}
                                            </span>
                                            <span style={factValueStyle}>
                                                {fact.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {description && (
                            <section style={sectionStyle}>
                                <h3 style={sectionTitleStyle}>
                                    Beschreibung
                                </h3>
                                <p style={descriptionStyle}>
                                    {displayedText}
                                </p>

                                {isLongText && (
                                    <button
                                        type="button"
                                        onClick={() => setExpanded(e => !e)}
                                        style={expandButtonStyle}
                                    >
                                        {expanded
                                            ? "▲ Weniger anzeigen"
                                            : "▼ Mehr anzeigen"}
                                    </button>
                                )}
                            </section>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}


const overlayStyle = {
    position: "absolute",
    inset: 0,
    background: "rgba(255,255,255,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10
};

const panelStyle = {
    position: "relative",
    display: "flex",
    width: "80%",
    height: "95%",
    maxHeight: "90vh",
    background: "#fff",
    border: "1px solid #eaeaea",
    overflow: "hidden",
    boxShadow: "0 20px 60px rgba(0,0,0,0.05)",
    color: "#111"
};

const imageColStyle = {
    position: "relative",
    flex: 1,
    minWidth: 0,
    height: "100%",
    background: "#f5f5f5",
    overflow: "hidden"
};

const imageStyle = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block"
};

const textColStyle = {
    width: "35%",
    height: "100%",
    padding: "28px 22px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden"
};

const titleContainerStyle = {
    flexShrink: 0,
    paddingRight: "25px",
    marginBottom: "14px"
};

const titleStyle = {
    margin: 0,
    fontSize: "clamp(14px, 1.8vw, 24px)",
    fontWeight: 600,
    letterSpacing: "-0.5px",
    lineHeight: 1.3,
    color: "#111",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
};

const categoriesStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginBottom: "18px",
    flexShrink: 0
};

const sectionStyle = {
    marginBottom: "24px"
};

const descriptionStyle = {
    fontSize: "12px",
    lineHeight: 1.8,
    color: "#555",
    margin: 0,
    whiteSpace: "pre-line",
    overflowWrap: "anywhere"
};

const expandButtonStyle = {
    border: "none",
    background: "none",
    color: "#777",
    cursor: "pointer",
    padding: 0,
    marginTop: "12px",
    fontSize: "11px",
    textAlign: "left"
};

const factsSectionStyle = {
    marginBottom: "50px"
};

const sectionTitleStyle = {
    fontSize: "11px",
    letterSpacing: "1px",
    color: "black",
    margin: "0 0 16px"
};

const factsListStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "12px"
};

const factRowStyle = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.2fr)",
    gap: "12px",
    alignItems: "start"
};

const factLabelStyle = {
    fontSize: "11px",
    color: "black",
    overflowWrap: "anywhere"
};

const factValueStyle = {
    fontSize: "11px",
    color: "black",
    textAlign: "right",
    overflowWrap: "anywhere"
};

const navButtonStyle = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    border: "none",
    background: "transparent",
    color: "#111",
    fontSize: "40px",
    lineHeight: 1,
    cursor: "pointer"
};

const dotsStyle = {
    position: "absolute",
    bottom: "16px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    gap: "8px"
};

const dotStyle = {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    background: "white",
    cursor: "pointer"
};

const closeButtonStyle = {
    position: "absolute",
    top: "16px",
    right: "16px",
    width: "36px",
    height: "36px",
    border: "none",
    background: "transparent",
    color: "#111",
    fontSize: "22px",
    lineHeight: 1,
    cursor: "pointer",
    zIndex: 2
};

const scrollContentStyle = {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarGutter: 'stable',
    paddingRight: '12px',
    boxSizing: 'border-box',
};
